import { useState, useRef, type ReactNode } from "react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SingleDatePicker } from "@/components/shared/SingleDatePicker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { parseAmazonMyeCsv } from "@/utils/amazonMyeCsv";
import { TruncatedText } from "@/components/ui/truncated-text";
import type { AmazonExperimentMetrics, AmazonMetricValue, TestOutcome } from "@/types/ab-tests";

type ActionChoice = "apply-b" | "keep-a" | "";

/**
 * WEB-002 (`validator/disclosure-fields-and-empty-states`): the parsed-preview must keep its
 * full shape too -- every known metric renders its labeled row, and a missing side or a fully
 * unresolved metric renders a dash + a short reason instead of the row vanishing.
 */
const NOT_FOUND_REASON = "Not found in this upload";

function EmptyPreviewValue() {
  return (
    <span className="text-muted-foreground cursor-help" title={NOT_FOUND_REASON}>
      —
    </span>
  );
}

function formatPreviewSide(value: number | string | null | undefined, numeric: boolean): ReactNode {
  if (value === null || value === undefined) return <EmptyPreviewValue />;
  return numeric ? (value as number).toLocaleString() : value;
}

interface PreviewRow {
  label: string;
  metric: AmazonMetricValue<number | string> | null | undefined;
  numeric: boolean;
  showDiff?: boolean;
}

function previewRows(parsed: AmazonExperimentMetrics): PreviewRow[] {
  return [
    { label: "Conversion", metric: parsed.conversion, numeric: false, showDiff: true },
    { label: "Units Per Unique Visitor", metric: parsed.unitsPerUniqueVisitor, numeric: true },
    { label: "Units Sold", metric: parsed.unitsSold, numeric: true },
    { label: "Units Sold From Search", metric: parsed.unitsSoldFromSearch, numeric: true },
    { label: "Sales", metric: parsed.sales, numeric: false },
    { label: "Sales From Search", metric: parsed.salesFromSearch, numeric: false },
    { label: "Sample Size", metric: parsed.sampleSize, numeric: true },
  ];
}

interface UploadResultsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId: string;
  testName: string;
  onUpload: (testId: string, file: File, outcome?: TestOutcome, variantBSwitchDate?: string) => Promise<void>;
}

export function UploadResultsModal({ open, onOpenChange, testId, testName, onUpload }: UploadResultsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<AmazonExperimentMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [actionChoice, setActionChoice] = useState<ActionChoice>("");
  const [switchDate, setSwitchDate] = useState<Date | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
    setParsed(null);
    setActionChoice("");
    setSwitchDate(undefined);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const result = parseAmazonMyeCsv(text);
      if (result) {
        setParsed(result);
      } else {
        setError(
          "Could not parse metrics from this CSV. Please ensure it matches the Amazon Experiment Report format.",
        );
      }
    };
    reader.readAsText(f);
  };

  const canSave =
    !!parsed &&
    actionChoice !== "" &&
    (actionChoice === "keep-a" || (actionChoice === "apply-b" && !!switchDate));

  const handleSubmit = async () => {
    if (!file || !parsed || !actionChoice) return;
    const outcome: TestOutcome = actionChoice === "apply-b" ? "variant" : "control";
    const dateStr = actionChoice === "apply-b" && switchDate ? format(switchDate, "yyyy-MM-dd") : undefined;
    setIsUploading(true);
    try {
      await onUpload(testId, file, outcome, dateStr);
      handleClose();
    } catch (_) {
      void _;
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsed(null);
    setError(null);
    setIsUploading(false);
    setActionChoice("");
    setSwitchDate(undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-card">
        <DialogHeader>
          <DialogTitle>Upload Experiment Results</DialogTitle>
          <DialogDescription className="break-words">
            Upload an Amazon Experiment Report CSV for &quot;{testName}&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div
            className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
            {!file ? (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">Click to upload CSV</p>
                <p className="text-xs text-muted-foreground">Amazon Experiment Report format</p>
              </div>
            ) : (
              <div className="flex items-center justify-start gap-2 min-w-0 w-full max-w-full px-1">
                <FileText className="h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm font-medium min-w-0 flex-1 text-left">
                  <TruncatedText>{file.name}</TruncatedText>
                </span>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 text-destructive text-sm bg-destructive/10 rounded-md p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="break-words">{error}</span>
            </div>
          )}

          {parsed && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--chart-8))]">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Preview parsed successfully
              </div>
              <div className="bg-muted/50 rounded-md p-3 text-sm space-y-1 w-max min-w-full">
                {previewRows(parsed).map((row) => (
                  <div key={row.label} className="flex justify-between gap-4">
                    <span className="text-muted-foreground shrink-0">{row.label}</span>
                    <span className="tabular-nums text-right">
                      {formatPreviewSide(row.metric?.a, row.numeric)} / {formatPreviewSide(row.metric?.b, row.numeric)}
                      {row.showDiff && <> ({formatPreviewSide(row.metric?.diff, row.numeric)})</>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action — required choice (only after successful parse) */}
          {parsed && (
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground">Action</h3>
                <p className="text-xs text-muted-foreground">Choose your action.</p>
              </div>
              <RadioGroup
                value={actionChoice}
                onValueChange={(v) => setActionChoice(v as ActionChoice)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="action-apply-b" value="apply-b" />
                  <Label htmlFor="action-apply-b" className="text-sm font-medium cursor-pointer">
                    Submit Variant B to change the listing
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="action-keep-a" value="keep-a" />
                  <Label htmlFor="action-keep-a" className="text-sm font-medium cursor-pointer">
                    Leave listing as control (keep Variant A)
                  </Label>
                </div>
              </RadioGroup>

              {actionChoice === "apply-b" && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">When did you submit this change?</Label>
                  <SingleDatePicker
                    date={switchDate}
                    onDateChange={setSwitchDate}
                    placeholder="Pick a date"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSave || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
